import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, Stepper, Step, StepLabel, Button } from '@mui/material';
import ChooseInputs from './steps/choose-inputs';
import WriteExpressions from './steps/write-expressions';
import ChooseActivation from './steps/choose-activation';
import ChooseReactions from './steps/choose-reactions';
import { useTranslation } from 'react-i18next';
import { useSensorStreamController } from '../../../sensors/sensors-stream';
import { StepperProvider, useStepperController } from './steps/step-backbone';
import { StreamRecorderProvider } from '../../../../../devices/charts/use-stream-recorder';
import { useOnlineDeviceStream, useOnlineDeviceIds } from '../../../live/online-device-provider';

const steps = [
  {
    label: 'MOVE_CONF.CHOOSE_INPUT_STEP',
    content: (props) => <ChooseInputs {...props} />
  }, {
    label: 'MOVE_CONF.MATHS_STEP',
    content: (props) => <WriteExpressions {...props} />
  }, {
    label: 'MOVE_CONF.ACTIVATES_STEP',
    content: (props) => <ChooseActivation {...props} />
  }, {
    label: 'MOVE_CONF.REACTIONS_STEP',
    content: (props) => <ChooseReactions {...props} />
  }
];

export const useConfigureManualMovementPhaseController = () => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return [open, handleOpen, handleClose];
}

function StepperFooter({ handleBack, handleNext, backDisabledOverride }) {
  const { t } = useTranslation();
  const { backDisabled, nextDisabled } = useStepperController();

  return (
    <DialogActions sx={{ justifyContent: 'flex-start', borderTop: 1, borderColor: 'divider' }}>
      <Button variant="outlined" disabled={Boolean(backDisabled) || backDisabledOverride} onClick={handleBack}>{t('MOVE_CONF.BACK_BUTTON')}</Button>
      <Button variant="contained" disabled={Boolean(nextDisabled)} onClick={handleNext}>{t('MOVE_CONF.NEXT_BUTTON')}</Button>
    </DialogActions>
  );
}

export default function ManualMovementPhaseDialog({ open, onClose, ...props }) {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = React.useState(0);
  const { start, stop } = useSensorStreamController();
  const { dataset, emitter } = useOnlineDeviceStream();
  const { serialNumber } = useOnlineDeviceIds();

  useEffect(() => {
    if (open) {
      start();
    } else {
      stop();
    }
  }, [open]);

  const handleClose = () => {
    onClose();
    setActiveStep(0);
  };

  const handleNext = () => {
    if (activeStep + 1 >= steps.length) {
      handleClose();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep - 1 < 0) {
      handleClose();
    } else {
      setActiveStep(activeStep - 1);
    }
  };

  return (
    <Dialog fullWidth maxWidth='xl' open={open} onClose={handleClose}>
      <StreamRecorderProvider emitter={emitter} deviceId={serialNumber} sensors={dataset} onComplete={() => {}} onAbort={() => {}}>
        <StepperProvider>
          <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Stepper activeStep={activeStep}>
              {steps.map((step) => (
                <Step key={step.label}>
                  <StepLabel>
                    {t(step.label)}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </DialogTitle>
          <DialogContent sx={{ height: '70vh', display: 'flex', flexDirection: 'column', padding: 0, margin: 0 }}>
            {steps[activeStep].content({ ...props })}
          </DialogContent>
          <StepperFooter handleBack={handleBack} handleNext={handleNext} backDisabledOverride={activeStep === 0} />
        </StepperProvider>
      </StreamRecorderProvider>
    </Dialog>
  );
}

ManualMovementPhaseDialog.propTypes = {
  open: propTypes.bool,
  onClose: propTypes.func,
}